import { isPlainObject } from "es-toolkit";
import { pick } from "es-toolkit/compat";
import { useCallback, useMemo, useState } from "react";

import { createLocalStorageCollectionFieldAccessor } from "./localStorageCollectionFieldAccessor.ts";
import { COLLECTION_KEY, defaultConstruct } from "./shared.ts";

export const useStickyState = <T, ST = T extends Record<string, unknown> ? Partial<T> : T>(
  name: string,
  initialState: T,
  construct: (value: unknown) => T = defaultConstruct,
): [T, (_: ST) => void, () => void] => {
  const accessor = useMemo(
    () =>
      createLocalStorageCollectionFieldAccessor<Record<string, T>, string>(
        COLLECTION_KEY,
        name,
        initialState,
        construct,
      ),
    [initialState, name, construct],
  );

  const [state, setState] = useState(() => {
    if (isPlainObject(initialState)) {
      const stored = accessor.get();

      const retain = pick(stored, Object.keys(initialState as Record<string, unknown>));

      const merged = { ...initialState, ...retain };

      accessor.set(merged);

      return merged;
    }

    return accessor.get();
  });

  const updateState = useCallback(
    (value: ST) => {
      const merged = isPlainObject(value) ? { ...accessor.get(), ...value } : (value as unknown as T);

      accessor.set(merged);
      setState(merged);
    },
    [accessor],
  );

  const removeState = useCallback(() => {
    accessor.remove();
    setState(initialState);
  }, [accessor, initialState]);

  return useMemo(() => [state, updateState, removeState], [removeState, state, updateState]);
};
